import React, { useState, useEffect } from 'react';
import { workoutHistoryService, type WorkoutHistoryEntry } from '../services/workoutHistory';
import { CommentDisplay } from './CommentDisplay';

interface WorkoutDetailProps {
  workoutId: string;
  onBack: () => void;
  onDelete?: (workoutId: string) => void;
}

/**
 * WorkoutDetail - Component for displaying detailed workout information
 * Shows complete workout data with exercises, sets, and statistics
 * Requirements: 7.1, 7.2, 7.5
 */
export const WorkoutDetail: React.FC<WorkoutDetailProps> = ({
  workoutId,
  onBack,
  onDelete
}) => {
  const [workoutData, setWorkoutData] = useState<WorkoutHistoryEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadWorkoutDetail();
  }, [workoutId]);

  const loadWorkoutDetail = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await workoutHistoryService.getWorkoutDetails(workoutId);
      setWorkoutData(data);

    } catch (err) {
      console.error('Failed to load workout detail:', err);
      setError('Не удалось загрузить детали тренировки');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!workoutData) return;

    try {
      await workoutHistoryService.deleteWorkout(workoutId);
      onDelete?.(workoutId);
      onBack();
    } catch (err) {
      console.error('Failed to delete workout:', err);
      alert('Не удалось удалить тренировку');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours} ч ${remainingMinutes} мин`;
    }
    return `${minutes} мин`;
  };

  if (isLoading) {
    return (
      <div className="workout-detail p-4 max-w-4xl mx-auto">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка деталей тренировки...</p>
        </div>
      </div>
    );
  }

  if (error || !workoutData) {
    return (
      <div className="workout-detail p-4 max-w-4xl mx-auto">
        <div className="text-center py-8">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">{error || 'Тренировка не найдена'}</p>
          <button
            onClick={onBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Вернуться к истории
          </button>
        </div>
      </div>
    );
  }

  const { workout, exercises, totalVolume, totalSets, totalReps, uniqueExercises } = workoutData;

  return (
    <div className="workout-detail p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Назад к истории
        </button>

        {onDelete && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-800 p-2"
            title="Удалить тренировку"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Workout Info */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h1 className="text-2xl font-bold mb-4">{workout.workout_name}</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Date and Time */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Дата и время</h3>
            <div className="space-y-1 text-gray-600">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDate(workout.completed_at)}
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatTime(workout.completed_at)}
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Длительность</h3>
            <div className="text-2xl font-bold text-blue-600">
              {formatDuration(workout.duration_minutes)}
            </div>
          </div>
        </div>

        {/* Workout Notes */}
        {workout.notes && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">Комментарий к тренировке</h3>
            <CommentDisplay
              comment={workout.notes}
              type="workout"
              timestamp={workout.completed_at}
              showTimestamp={false}
            />
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Статистика тренировки</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{uniqueExercises}</div>
            <div className="text-sm text-blue-600">упражнений</div>
          </div>
          
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{totalSets}</div>
            <div className="text-sm text-green-600">подходов</div>
          </div>
          
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{totalReps}</div>
            <div className="text-sm text-purple-600">повторений</div>
          </div>
          
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {totalVolume > 0 ? `${totalVolume}кг` : '—'}
            </div>
            <div className="text-sm text-orange-600">объём</div>
          </div>
        </div>
      </div>

      {/* Exercises */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Упражнения ({exercises.length})</h2>
        
        {exercises.map((exerciseData, index) => {
          const { exercise, sets, totalVolume: exerciseVolume, maxWeight, totalReps: exerciseReps } = exerciseData;
          
          return (
            <div key={exercise.id} className="bg-white border border-gray-200 rounded-lg p-4">
              {/* Exercise Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {index + 1}. {exercise.exerciseInfo.name_ru}
                  </h3>
                  <div className="text-sm text-gray-600 mt-1">
                    {exercise.exerciseInfo.muscle_group}
                    {exercise.exerciseInfo.equipment && ` • ${exercise.exerciseInfo.equipment}`}
                  </div>
                </div>
                
                {/* Exercise Stats */}
                <div className="text-right text-sm text-gray-600">
                  <div>{sets.length} подходов</div>
                  <div>{exerciseReps} повторений</div>
                  {maxWeight > 0 && <div>макс. {maxWeight}кг</div>}
                  {exerciseVolume > 0 && <div>{exerciseVolume}кг объём</div>}
                </div>
              </div>

              {/* Exercise Notes */}
              {exercise.notes && (
                <div className="mb-4">
                  <CommentDisplay
                    comment={exercise.notes}
                    type="exercise"
                    compact={true}
                  />
                </div>
              )}

              {/* Sets */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Подходы:</h4>
                
                {sets.map((set) => (
                  <div key={set.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium text-gray-600">#{set.set_number}</span>
                      <span className="font-medium">
                        {set.weight > 0 ? `${set.weight}кг` : 'Собственный вес'} × {set.reps}
                      </span>
                      {set.assistance && (
                        <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">
                          {set.assistance}
                        </span>
                      )}
                    </div>
                    
                    <div className="text-right">
                      {set.notes && (
                        <div className="text-sm text-gray-600 italic">"{set.notes}"</div>
                      )}
                      <div className="text-xs text-gray-400">
                        {new Date(set.completed_at).toLocaleTimeString('ru-RU', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Удалить тренировку?</h3>
            <p className="text-gray-600 mb-6">
              Это действие нельзя отменить. Все данные о тренировке будут удалены навсегда.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700"
              >
                Удалить
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutDetail;